// One form, two doors (DESIGN-VISUAL.md §21, docs/spec/CR-007.md). `door="staff"` renders a
// required TOTP code field and sends it in the same `auth_login` request as the password — never a
// first call with only the password to learn whether a code is needed, which would record a failed
// staff attempt on every ordinary sign-in (DESIGN.md §8.1 *The two doors*). `door="investor"` has no
// code field in the markup at all: `/masuk` neither asks for nor accepts a second factor. Until the
// request answers 200, nothing protected is fetched or shown: this component calls only `login()` on
// submit and renders only its own fields and status notice.
'use client';

import { useId, useState, type FormEvent } from 'react';
// Relative, not `@/` — `frontend/vitest.config.ts` (T-03's, frozen) has no path-alias resolution.
import { Button } from '../../../components/ui/Button';
import { Field } from '../../../components/ui/Field';
import { Input } from '../../../components/ui/Input';
import { NoticeBand } from '../../../components/ui/NoticeBand';
import { PasswordInput } from '../../../components/ui/PasswordInput';
import { login, type AuthenticatedUser } from '../api';
import { getApiError } from '../errors';

export interface SignInFormProps {
  onSuccess: (user: AuthenticatedUser) => void;
  door: 'investor' | 'staff';
}

type Status = { kind: 'idle' } | { kind: 'submitting' } | { kind: 'error'; message: string };

const GENERIC_ERROR = 'Masuk tidak berhasil. Periksa email, kata sandi, dan kode verifikasi Anda.';

const NETWORK_ERROR = 'Tidak dapat menghubungi server. Periksa koneksi Anda lalu coba lagi.';

export function SignInForm({ onSuccess, door }: SignInFormProps) {
  const emailId = useId();
  const passwordId = useId();
  const totpId = useId();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [status, setStatus] = useState<Status>({ kind: 'idle' });

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setStatus({ kind: 'submitting' });
    // Exactly one `auth_login` request per submission, password and TOTP code together
    // (DESIGN.md §8.1) — never a first call with only the password to learn whether a code is
    // required, which the design explicitly forbids because it would record a failed staff
    // attempt on every ordinary sign-in.
    let result: Awaited<ReturnType<typeof login>>;
    try {
      result = await login({ email, password, door, totpCode: totpCode || undefined });
    } catch {
      // The request never produced a response (network down, backend unreachable, blocked). Without
      // this the promise rejects unhandled and the form stays on "Memeriksa…" with no message.
      setStatus({ kind: 'error', message: NETWORK_ERROR });
      return;
    }
    const { data, error, response } = result;
    if (error) {
      const apiError = getApiError(error);
      const retryAfter = response.headers.get('Retry-After');
      const suffix = retryAfter ? ` Coba lagi setelah ${retryAfter} detik.` : '';
      setStatus({ kind: 'error', message: `${apiError?.message ?? GENERIC_ERROR}${suffix}` });
      return;
    }
    if (!data) {
      setStatus({ kind: 'error', message: GENERIC_ERROR });
      return;
    }
    setStatus({ kind: 'idle' });
    onSuccess(data);
  }

  const submitting = status.kind === 'submitting';

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-s5" noValidate>
      {status.kind === 'error' ? (
        <NoticeBand tone="attention" title="Tidak dapat masuk">
          {status.message}
        </NoticeBand>
      ) : null}

      <Field id={emailId} label="Email" required>
        <Input
          id={emailId}
          name="email"
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />
      </Field>

      <Field id={passwordId} label="Kata sandi" required>
        <PasswordInput
          id={passwordId}
          name="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(event) => setPassword(event.target.value)}
        />
      </Field>

      {door === 'staff' ? (
        <Field
          id={totpId}
          label="Kode verifikasi dua faktor"
          hint="Wajib untuk akun ppa_staff, ops, dan superadmin. Masukkan kode dari aplikasi autentikator Anda."
          required
        >
          <Input
            id={totpId}
            name="totp_code"
            inputMode="numeric"
            pattern="[0-9]{6}"
            maxLength={6}
            autoComplete="one-time-code"
            required
            aria-describedby={`${totpId}-hint`}
            value={totpCode}
            onChange={(event) => setTotpCode(event.target.value.replace(/[^0-9]/g, ''))}
          />
        </Field>
      ) : null}

      <Button type="submit" variant="primary" loading={submitting} loadingLabel="Memeriksa…" fullWidth>
        Masuk
      </Button>
    </form>
  );
}
